import { useMemo } from 'react';
import { Area, AreaChart, CartesianGrid, XAxis, YAxis } from 'recharts';
import { format, parseISO } from 'date-fns';

import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { ChartContainer, ChartTooltip, ChartTooltipContent } from '@/components/ui/chart';
import { Skeleton } from '@/components/ui/skeleton';
import { useJobStats } from '@/hooks/useJobs';

// How many trailing days of the byDate series to plot.
const WINDOW_DAYS = 30;

const chartConfig = {
  jobs: { label: 'Jobs added' },
};

export function JobsTrendChart() {
  const { data, isLoading, isError } = useJobStats();

  const trend = useMemo(() => {
    if (!data?.byDate) return [];
    return data.byDate.slice(-WINDOW_DAYS).map(({ date, count }) => ({
      // `date` is an ISO 'YYYY-MM-DD' day bucket from the stats query.
      day: format(parseISO(date), 'MMM dd'),
      jobs: Number(count) || 0,
    }));
  }, [data]);

  return (
    <Card className="lg:col-span-2">
      <CardHeader>
        <CardTitle>Jobs Added</CardTitle>
        <CardDescription>New jobs added per day over the last {WINDOW_DAYS} days</CardDescription>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <Skeleton className="h-[260px] w-full" />
        ) : isError ? (
          <p className="flex h-[260px] items-center justify-center text-sm text-muted-foreground">
            Couldn’t load the jobs trend.
          </p>
        ) : trend.length === 0 ? (
          <p className="flex h-[260px] items-center justify-center text-sm text-muted-foreground">
            No jobs added yet.
          </p>
        ) : (
          <ChartContainer config={chartConfig} className="aspect-auto h-[260px] w-full">
            <AreaChart data={trend} margin={{ left: -12, right: 8, top: 8 }}>
              <defs>
                <linearGradient id="fillJobs" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="var(--chart-1)" stopOpacity={0.35} />
                  <stop offset="95%" stopColor="var(--chart-1)" stopOpacity={0.02} />
                </linearGradient>
              </defs>
              <CartesianGrid vertical={false} strokeDasharray="3 3" />
              <XAxis dataKey="day" tickLine={false} axisLine={false} tickMargin={8} minTickGap={24} />
              <YAxis tickLine={false} axisLine={false} width={36} allowDecimals={false} />
              <ChartTooltip content={<ChartTooltipContent />} />
              <Area
                dataKey="jobs"
                type="monotone"
                stroke="var(--chart-1)"
                strokeWidth={2}
                fill="url(#fillJobs)"
              />
            </AreaChart>
          </ChartContainer>
        )}
      </CardContent>
    </Card>
  );
}
